import { IFramework } from './IFramework';
import utilities from './Services/Utilities';
import { WebServiceManager } from './Services/WebServiceManager';
import { IUserContext } from './IUserContext';
import { IBaseServiceRegistration } from './Utilities/IBaseServiceRegistration';
import { IServiceRegistration } from './Utilities/IServiceRegistration';
import { ServiceType } from './Utilities/ServiceType';
import { ActivatorType } from './Utilities/ActivatorType';
import { UserContextFactory as contextFactory } from './UserContextFactory';

//MODULE

class Framework implements IFramework {
    constructor() {
        this._contextLoaded = false;
        this._initializers = [];
        this.isInitialized = false;

        this._registrations = new Map();
        this._namedRegistrations = new Map();

        this.registerDefaultSevices();

        this._userContext = this.loadContext();
    }

    private registerDefaultSevices() {
        this.registerService('IUtilities', utilities);
        this.registerTransient('IWebServiceManager', (framework) => {
            return new WebServiceManager(framework.context);
        });
    }

    private _contextLoaded: boolean;

    private _initializers: ((framework: IFramework) => void)[];

    private _regCounter: number = 0;
    private _registrations: Map<number, IBaseServiceRegistration>;
    private _namedRegistrations: Map<string, number[]>;

    private _userContext: IUserContext;

    private loadContext(): IUserContext {
        if (!this._contextLoaded) {
            this.loadContextFromDom();
            if (!this._contextLoaded) {
                this._userContext = contextFactory();
                this.onDomLoaded((f) => { (f as Framework).loadContextFromDom(); });
            }
        }
        return this._userContext;
    }

    private loadContextFromDom() {
        let tdContext = this.getMeta('context-data');
        if (tdContext && tdContext !== null) {
            this._userContext = contextFactory(tdContext);
            this._contextLoaded = true;
            console.log('User context loaded.');
        }
    }

    private getMeta(metaName: string): string | null {
        const metas = document.getElementsByTagName('meta');
        for (let i = 0; i < metas.length; i++) {
            if (metas[i].getAttribute('name') === metaName) {
                return metas[i].getAttribute('content') || null;
            }
        }
        return null;
    }

    isInitialized: boolean;

    initialize() {
        if (!this.isInitialized) {
            console.log('Framework Initializing ...');

            let initializers = this._initializers;
            this._initializers = [];
            for (let i = 0; i < initializers.length; i++) {
                initializers[i](this);
            }

            this.isInitialized = true;
        } else {
            throw 'Framework already initialized.';
        }
    }

    addInitializer(func: (framework: IFramework) => void) {
        if (this.isInitialized) {
            func(this);
        } else {
            this._initializers.push(func);
        }
    }

    onDomLoaded(func: (framework: IFramework) => void) {
        if (document.readyState === 'loading') {
            var framework = this;
            document.addEventListener("DOMContentLoaded", function (e) {
                func(framework);
            });
        } else {
            func(this);
        }
    }

    registerTransient<T>(name: string, activator: ActivatorType<T>) {
        let registration = {
            Name: name,
            Type: ServiceType.Transient,
            Activator: activator
        } as IServiceRegistration<T>;
        return this.register(name, registration);
    }

    registerService<T>(name: string, service: T | ActivatorType<T>) {
        let registration: IServiceRegistration<T>;
        if (typeof service === 'function') {
            registration = {
                Name: name,
                Type: ServiceType.Singleton,
                Activator: service as ActivatorType<T>
            };
        } else {
            registration = {
                Name: name,
                Type: ServiceType.Singleton,
                Instance: service as T
            };
        }
        return this.register(name, registration);
    }

    register(name: string, registration: IBaseServiceRegistration): number {
        let counter = this._regCounter++;
        registration.Name = name;
        this._registrations.set(counter, registration);
        let reg = this._namedRegistrations.get(name);
        if (reg) {
            reg.push(counter);
        } else {
            this._namedRegistrations.set(name, [counter]);
        }
        return counter;
    }

    getRegistration(name: string): IBaseServiceRegistration | null {
        let reg = this._namedRegistrations.get(name);
        if (reg && reg.length > 0) {
            let regNumber = reg[reg.length - 1];
            let itm = this._registrations.get(regNumber);
            if (itm) {
                return itm;
            }
        }
        return null;
    }

    removeRegistration(name: string | number): void {
        if (typeof name as any === 'string' || (name as any) instanceof String) {
            this.removeNamedRegistration(name as string);
        } else {
            this.removeCounterRegistration(name as number);
        }
    }

    private removeNamedRegistration(name: string): void {
        let reg = this._namedRegistrations.get(name);

        if (reg) {
            this._namedRegistrations.delete(name);

            for (let i = 0; i < reg.length; i++) {
                this._registrations.delete(reg[i]);
            }
        }
    }

    private removeCounterRegistration(counter: number): void {
        let reg = this._registrations.get(counter);
        if (reg) {
            let named = this._namedRegistrations.get(reg.Name);
            if (named) {
                let idx = named.indexOf(counter);
                if (idx > -1) {
                    named.splice(idx, 1);
                }
            }

            this._registrations.delete(counter);
        }
    }

    private getRegisteredService<T>(registration: IServiceRegistration<T> | null): T | null {
        if (registration == null) return null;

        if (registration.Type === ServiceType.Transient) {
            if (registration.Activator && registration.Activator !== null) {
                return registration.Activator(this);
            }
        } else {
            if (registration.Instance && registration.Instance !== null) {
                return registration.Instance;
            } else if (registration.Activator && registration.Activator !== null) {
                registration.Instance = registration.Activator(this);
                return registration.Instance;
            }
        }
        return null;
    }

    getServices<T>(name: string): T[] {
        let frame = this;
        let reg = this._namedRegistrations.get(name);
        if (reg && reg.length > 0) {
            return reg.map(function (counter) {
                let registration = frame._registrations.get(counter) as IServiceRegistration<T> | null;
                return frame.getRegisteredService(registration);
            }).filter(function (reg) {
                return reg !== null;
            }) as T[];
        }
        return [];
    }

    getService<T>(name: string): T | null {
        var registration = this.getRegistration(name) as IServiceRegistration<T> | null;
        return this.getRegisteredService(registration);
    }

    getRequiredService<T>(name: string): T {
        let service = this.getService<T>(name);
        if (service === null)
            throw 'Could not find service ' + name + '.';
        return service;
    }

    updateContext(context: IUserContext | string): void {
        if (context) {
            if (typeof context === 'string') {
                let meta = context as string;
                this.setContext(this.getContext(meta));

            } else {
                this.setContext(context as IUserContext);
            }
        }
    }

    private getContext(meta: string) {
        return contextFactory(meta);
    }

    private setContext(context: IUserContext) {
        this._userContext = context;
        this._contextLoaded = true;
    }

    get services(): string[] {
        return Array.from(this._namedRegistrations.keys());
    }

    get context(): IUserContext {
        return this._userContext;
    }
}

var __localFramework = new Framework() as IFramework;
export default __localFramework;